import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import toast from "react-hot-toast";
import { convertToBackendDateFormat } from "../../../../shared/utils/dateUtils";
import type { BuildingOption } from "../../../../api/client/types/common.types";
import { vm } from "../../../../shared/utils/validationMessages";
import { buildReportFileName } from "../../utils/reportFilenames";

// DateRangePicker sends YYYY-MM-DD; we also allow DD/MM/YYYY for flexibility.
const dateFormatRegex = /^(\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4})$/;

function parseDateString(value: string): { year: number; month: number; day: number } {
  if (value.includes("-")) {
    const [y, m, d] = value.split("-").map(Number);
    return { year: y, month: m, day: d };
  }
  const [d, m, y] = value.split("/").map(Number);
  return { year: y, month: m, day: d };
}

function toDate(value: string): Date {
  const { year, month, day } = parseDateString(value);
  return new Date(year, month - 1, day);
}

export const managementReportFormSchema = z
  .object({
    start_date: z
      .string()
      .min(1, { message: vm.START_DATE_REQUIRED })
      .regex(dateFormatRegex, { message: vm.DATE_FORMAT_INVALID }),
    end_date: z
      .string()
      .min(1, { message: vm.END_DATE_REQUIRED })
      .regex(dateFormatRegex, { message: vm.DATE_FORMAT_INVALID }),
    building_id: z.string().min(1, { message: vm.REPORT_BUILDING_REQUIRED }),
    includeYearSummary: z.boolean(),
  })
  .refine(
    (data) => {
      const startDate = toDate(data.start_date);
      const endDate = toDate(data.end_date);
      return startDate <= endDate;
    },
    {
      message: vm.START_DATE_BEFORE_END,
      path: ["start_date"],
    }
  )
  .refine(
    (data) => {
      const startDate = toDate(data.start_date);
      const endDate = toDate(data.end_date);
      const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays <= 32;
    },
    {
      message: vm.DATE_RANGE_MAX_32_DAYS,
      path: ["end_date"],
    }
  );

export type ManagementReportFormValues = z.infer<typeof managementReportFormSchema>;

interface UseManagementReportGeneratorProps {
  onGenerate: (
    start_date: string,
    end_date: string,
    building_id: string,
    includeYearSummary: boolean
  ) => Promise<Blob | null>;
  /** Full list of buildings (all available). Not filtered by date. */
  buildingOptions: BuildingOption[];
}

export const useManagementReportGenerator = ({
  onGenerate,
  buildingOptions,
}: UseManagementReportGeneratorProps) => {
  const form = useForm<ManagementReportFormValues>({
    resolver: zodResolver(managementReportFormSchema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: {
      start_date: "",
      end_date: "",
      building_id: "",
      includeYearSummary: false,
    },
  });

  const { watch, setValue, handleSubmit, formState } = form;
  const { errors, isValid } = formState;

  const handleDateRangeChange = (newStartDate: string, newEndDate: string) => {
    setValue("start_date", newStartDate, { shouldValidate: false });
    setValue("end_date", newEndDate, { shouldValidate: false });
  };

  const toBackendDate = (value: string): string =>
    value.includes("-") ? convertToBackendDateFormat(value) : value;

  const onSubmit = async (data: ManagementReportFormValues) => {
    const startBackend = toBackendDate(data.start_date);
    const endBackend = toBackendDate(data.end_date);
    if (!startBackend || !endBackend) {
      toast.error(vm.DATE_FORMAT_INVALID);
      return;
    }

    const buildingName =
      buildingOptions.find((b) => b.id === data.building_id)?.name ?? "Unidad residencial";

    try {
      const blob = await onGenerate(
        startBackend,
        endBackend,
        data.building_id,
        data.includeYearSummary
      );
      if (blob !== null) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = buildReportFileName({
          reportType: "management",
          buildingName,
          startDate: startBackend,
          endDate: endBackend,
          extension: "pdf",
        });
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
      toast.success("Informe de gestión generado y descargado correctamente");
    } catch (error) {
      console.error("Error generating management report:", error);
      toast.error("Error al generar el informe. Por favor, intenta nuevamente.");
    }
  };

  return {
    form,
    startDate: watch("start_date"),
    endDate: watch("end_date"),
    errors: {
      start_date: errors.start_date?.message,
      end_date: errors.end_date?.message,
      building_id: errors.building_id?.message,
    },
    isValid,
    isSubmitting: formState.isSubmitting,
    handleDateRangeChange,
    handleGenerateReport: handleSubmit(onSubmit),
  };
};
